// SPDX-License-Identifier: BUSL-1.1

/**
 * Actor avatar — colored circle with first initial, deterministic hash-based color.
 *
 */

import type * as React from 'react';

interface ActorAvatarProps {
  /** Actor ID used for deterministic color hashing. */
  id: string;
  /** Display letter. Defaults to first character of `id` uppercased. */
  label?: string;
  /** Size: sm=20px, md=24px. Default md. */
  size?: 'sm' | 'md';
  /** System actor gets neutral gray instead of hash-based color. */
  system?: boolean;
}

function hashToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

export function ActorAvatar({ id, label, size = 'md', system }: ActorAvatarProps): React.ReactElement {
  const isEmpty = id.length === 0;
  const firstGrapheme = Array.from(id)[0] ?? '?';
  const displayChar = (label ?? (isEmpty ? '?' : firstGrapheme)).toUpperCase();
  const backgroundColor = system || isEmpty ? '#525252' : `hsl(${hashToHue(id)}, 60%, 50%)`;

  return (
    <span
      aria-label={`Actor ${id}`}
      title={id}
      style={{ backgroundColor }}
      className={`inline-flex items-center justify-center rounded-full font-bold text-white ${
        size === 'sm' ? 'size-5 text-[9px]' : 'size-6 text-[10px]'
      }`}
    >
      {displayChar}
    </span>
  );
}
